// RITA-1585 — Gemeinsamer Baustein von Leiste und Kachel: Kapitelauswahl, Suchfeld und die Überschrift des gewählten
// Kapitels. Darunter bleibt es leer, bis der Handbuchtext kommt (RITA-901) – ohne Hinweissatz, weil der Kunde das
// Handbuch erst mit dem Inhalt sieht (Entscheidung Julia 31.10.2026). Die Kachel zeigt zusätzlich die Handbuch-Auswahl
// und sucht über alle Handbücher; die Leiste bleibt beim Handbuch der Seite.
import React, { useState } from 'react';
import { Box, List, ListItemButton, ListItemText, TextField, Typography } from '@mui/material';
import {
  Handbuch,
  HANDBUECHER,
  handbuchFinden,
  handbuchSuchen,
  kapitelBezeichnung,
  kapitelFinden,
  Kapitelwahl,
} from '../model/handbuecher';

export const HANDBUCH_AUSWAHL_FELDNAME = 'Handbuch';
export const KAPITEL_AUSWAHL_FELDNAME = 'Kapitel';
export const HANDBUCH_SUCHE_LEISTE = 'Im Handbuch suchen …';
export const HANDBUCH_SUCHE_ALLE = 'In allen Handbüchern suchen …';
export const HANDBUCH_KEINE_TREFFER = 'Keine Treffer.';

interface Props {
  auswahl: Kapitelwahl;
  onAuswahl: (wahl: Kapitelwahl) => void;
  /** Kachel: Handbuch-Auswahl über der Kapitelauswahl, Suche über alle Handbücher. Leiste: nur das gewählte Handbuch. */
  alleHandbuecher?: boolean;
  handbuecher?: readonly Handbuch[];
  /** Überschrift des Kapitels: in der Leiste unter der Leistenüberschrift (h3), auf der Kachel unter dem Seitenkopf (h2). */
  ueberschrift?: 'h2' | 'h3';
}

export default function HandbuchGeruest({
  auswahl,
  onAuswahl,
  alleHandbuecher = false,
  handbuecher = HANDBUECHER,
  ueberschrift = 'h3',
}: Props) {
  const [suche, setSuche] = useState('');
  const gefunden = kapitelFinden(auswahl, handbuecher);
  const handbuch = gefunden?.handbuch ?? handbuchFinden(auswahl.handbuchId, handbuecher) ?? handbuecher[0];
  const suchbereich = alleHandbuecher ? handbuecher : [handbuch];
  const treffer = handbuchSuchen(suche, suchbereich);
  const sucheAktiv = suche.trim().length > 0;

  const handbuchWaehlen = (handbuchId: string) => {
    const ziel = handbuchFinden(handbuchId, handbuecher);
    if (ziel && ziel.kapitel.length > 0) onAuswahl({ handbuchId: ziel.id, kapitelId: ziel.kapitel[0].id });
  };
  const trefferWaehlen = (wahl: Kapitelwahl) => {
    onAuswahl(wahl);
    setSuche('');
  };

  return (
    <Box data-testid="handbuch-geruest" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {alleHandbuecher && (
        <TextField
          select
          size="small"
          label={HANDBUCH_AUSWAHL_FELDNAME}
          value={handbuch.id}
          onChange={(event) => handbuchWaehlen(event.target.value)}
          SelectProps={{ native: true }}
          inputProps={{ 'aria-label': HANDBUCH_AUSWAHL_FELDNAME, 'data-testid': 'handbuch-auswahl' }}
        >
          {handbuecher.map((eintrag) => (
            <option key={eintrag.id} value={eintrag.id}>
              {eintrag.titel}
            </option>
          ))}
        </TextField>
      )}
      <TextField
        select
        size="small"
        label={KAPITEL_AUSWAHL_FELDNAME}
        value={gefunden ? gefunden.kapitel.id : ''}
        onChange={(event) => onAuswahl({ handbuchId: handbuch.id, kapitelId: event.target.value })}
        SelectProps={{ native: true }}
        inputProps={{ 'aria-label': KAPITEL_AUSWAHL_FELDNAME, 'data-testid': 'handbuch-kapitel-auswahl' }}
      >
        {handbuch.kapitel.map((kapitel) => (
          <option key={kapitel.id} value={kapitel.id}>
            {kapitelBezeichnung(kapitel)}
          </option>
        ))}
      </TextField>
      <TextField
        size="small"
        type="search"
        placeholder={alleHandbuecher ? HANDBUCH_SUCHE_ALLE : HANDBUCH_SUCHE_LEISTE}
        value={suche}
        onChange={(event) => setSuche(event.target.value)}
        inputProps={{
          'aria-label': alleHandbuecher ? HANDBUCH_SUCHE_ALLE : HANDBUCH_SUCHE_LEISTE,
          'data-testid': 'handbuch-suche',
        }}
      />
      {sucheAktiv ? (
        <Box data-testid="handbuch-treffer" aria-live="polite">
          {treffer.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {HANDBUCH_KEINE_TREFFER}
            </Typography>
          ) : (
            <List dense disablePadding aria-label="Treffer">
              {treffer.map((eintrag) => (
                <ListItemButton
                  key={`${eintrag.handbuch.id}/${eintrag.kapitel.id}`}
                  onClick={() => trefferWaehlen({ handbuchId: eintrag.handbuch.id, kapitelId: eintrag.kapitel.id })}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText primary={kapitelBezeichnung(eintrag.kapitel)} secondary={eintrag.handbuch.titel} />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      ) : null}
      {gefunden ? (
        <Typography
          component={ueberschrift}
          variant="subtitle1"
          data-testid="handbuch-kapitel-ueberschrift"
          sx={{ fontWeight: 600, mt: 0.5 }}
        >
          {kapitelBezeichnung(gefunden.kapitel)}
        </Typography>
      ) : null}
    </Box>
  );
}
